import { Archive, List, Plus } from "lucide-react";
import { useState } from "react";

import type { GtmBrainDoc } from "../api/GtmApi";
import { listKeywords } from "./keywords";

type Props = {
  docs: GtmBrainDoc[];
  onDocsChange: (docs: GtmBrainDoc[]) => void;
  onBackToOverview: () => void;
};

export default function BrainKeywordsPanel({ docs, onDocsChange, onBackToOverview }: Props) {
  const keywords = listKeywords(docs);
  const [draft, setDraft] = useState("");

  const addPhrase = () => {
    const phrase = draft.trim();
    if (!phrase) return;
    const exists = keywords.some((k) => k.phrase.toLowerCase() === phrase.toLowerCase());
    if (exists) {
      setDraft("");
      return;
    }
    onDocsChange([...docs, { docType: "keyword", phrase }]);
    setDraft("");
  };

  const archivePhrase = (index: number) => {
    onDocsChange(docs.filter((_, i) => i !== index));
  };

  return (
    <div className="flex h-full w-[22rem] shrink-0 flex-col border-r border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3 text-xs text-slate-500">
        <button
          type="button"
          className="hover:text-slate-800 hover:underline"
          onClick={onBackToOverview}
        >
          Brain
        </button>
        <span className="mx-1.5 text-slate-300">›</span>
        <span className="font-medium text-slate-800">Keywords</span>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        <div className="mb-1 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-lime-50">
            <List className="h-4 w-4 text-lime-700" />
          </span>
          <h2 className="text-base font-semibold text-slate-900">Keywords</h2>
        </div>
        <p className="mb-5 text-sm text-slate-500">The phrases I watch for your market.</p>

        {keywords.length === 0 ? (
          <p className="mb-4 rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-sm text-slate-500">
            No phrases yet. Add one below.
          </p>
        ) : (
          <ul className="mb-4 divide-y divide-slate-100 rounded-lg border border-slate-200">
            {keywords.map((k) => (
              <li key={k.key} className="flex items-center gap-2 px-3 py-2.5">
                <span className="min-w-0 flex-1 truncate text-sm text-slate-900">{k.phrase}</span>
                <button
                  type="button"
                  onClick={() => archivePhrase(k.index)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                  aria-label={`Archive ${k.phrase}`}
                >
                  <Archive className="h-3.5 w-3.5" />
                  Archive
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addPhrase();
              }
            }}
            placeholder="Add a phrase to watch"
            className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
          />
          <button
            type="button"
            onClick={addPhrase}
            disabled={!draft.trim()}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-2 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
          A saved phrase can&apos;t be renamed: archive it and add the new wording.
        </p>
      </div>
    </div>
  );
}
